import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';

const CANONICAL = 'https://e-asy.es/erp-para-pymes';

const SERVICE_LD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'ERP y software de gestión para pymes',
  description:
    'Consultoría, diseño e implantación de ERP y software de gestión para pymes que han crecido más allá de Excel y herramientas sueltas.',
  url: CANONICAL,
  serviceType: 'Consultoría tecnológica e implantación de ERP',
  provider: {
    '@type': 'Organization',
    name: 'E-ASY',
    url: 'https://e-asy.es',
  },
  areaServed: { '@type': 'Country', name: 'España' },
};

const FAQ = [
  {
    question: '¿Necesito un ERP a medida?',
    answer:
      'No siempre. A veces basta con ordenar procesos, integrar lo que ya tienes o configurar bien un ERP modular. Lo revisamos contigo: volumen de datos, usuarios, sectores y qué quieres dejar de hacer a mano.',
  },
  {
    question: '¿Vulpin sirve para pymes?',
    answer:
      'Sí. Vulpin 360 incluye el área Business pensada para pymes: gestión unificada y módulos que se activan según necesidad. Es nuestro ERP propio; si encaja, lo implantamos contigo. Si no, trabajamos con otras plataformas o integraciones.',
  },
  {
    question: '¿Se puede integrar con facturación o CRM?',
    answer:
      'Sí. Parte de nuestro trabajo habitual es integrar CRM, facturación, ecommerce o contabilidad para que los datos viajen una sola vez y no se reescriban en cada herramienta.',
  },
  {
    question: '¿Cómo es el proceso de implantación?',
    answer:
      'Empezamos por entender cómo trabajas hoy, qué datos son críticos y qué cuello de botella quieres quitar primero. Luego definimos alcance, formación y puesta en marcha por fases para no parar el negocio.',
  },
  {
    question: '¿Implantáis el ERP fuera de Tenerife?',
    answer:
      'Sí. El proyecto se hace en remoto con pymes de toda España. La oficina y la atención presencial están en Tenerife.',
  },
];

const ErpParaPymesPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="ERP para pymes en España | Software de gestión e implantación | E-ASY"
    description="ERP para pymes en España: software de gestión, implantación e integración cuando Excel y las herramientas sueltas se quedan cortos. Acompañamiento en remoto desde nuestra sede en Tenerife."
    keywords="ERP para pymes, ERP para pymes en España, software de gestión para pymes, implantación ERP, alternativa a Excel, integrar CRM y facturación"
    canonical={CANONICAL}
    h1="ERP para pymes: cuando la gestión ya no cabe en Excel"
    lede="Ayudamos a pymes que han crecido y necesitan un software de gestión que cuadre con su forma de trabajar: menos copiar y pegar, más trazabilidad y un solo sitio donde mirar números y pedidos."
    faqItems={FAQ}
    serviceJsonLd={SERVICE_LD}
    serviceJsonLdId="jsonld-service-erp-pymes"
  >
    <h2>Cuando Excel y varias herramientas dejan de ser suficientes</h2>
    <p>
      Es habitual: al principio bastan hojas de cálculo, un CRM ligero y el
      programa de facturación. Cuando suben pedidos, almacén o personas, aparecen
      versiones distintas del mismo dato y nadie sabe qué archivo es el bueno.
    </p>
    <p>
      Un <strong>ERP para pymes</strong> no es magia: es poner ventas,
      compras, stock y administración en una lógica compartida, con permisos y
      historial. Nosotros lo abordamos como consultoría: primero el problema,
      luego la herramienta.
    </p>

    <h2>Software de gestión para empresa: qué hacemos en E-ASY</h2>
    <ul>
      <li>
        Definir qué necesitas de verdad (no implantar “por tener ERP”).
      </li>
      <li>
        <strong>ERP a medida</strong> o modular según caso: desde nuestro ERP{' '}
        <Link to="/vulpin" className="vulpin-inline-link">
          Vulpin Suite
        </Link>{' '}
        (área Business) hasta integrar lo que ya usas.
      </li>
      <li>
        Integrar facturación, CRM, tienda online o contabilidad para que
        dejes de duplicar trabajo.
      </li>
    </ul>

    <h2>Integrar CRM y facturación sin peleas entre sistemas</h2>
    <p>
      Muchas pymes buscan exactamente esto: que el pedido nacido en el CRM
      llegue a albarán y factura sin reescribir líneas. Eso es{' '}
      <Link to="/integracion-sistemas">integración de sistemas</Link>, y va
      ligado casi siempre a un buen diseño de proceso, no solo a un conector.
    </p>

    <h2>ERP para pymes en toda España, con sede en Tenerife</h2>
    <p>
      La implantación, la formación y el seguimiento se hacen en remoto con
      equipos de toda España. La oficina está en Tenerife. El detalle está en{' '}
      <Link to="/donde-trabajamos">dónde trabajamos</Link>. Si buscas
      acompañamiento presencial en la isla, está en la página de{' '}
      <Link to="/consultoria-tecnologica-tenerife">
        consultoría tecnológica en Tenerife
      </Link>
      .
    </p>

    <p>
      <Link to="/contacto" className="seo-landing-cta">
        Ver si encaja para mi empresa
      </Link>
    </p>
  </SeoLandingLayout>
);

export default ErpParaPymesPage;
